// Copyright 2017 Sorama B.V.

import { autoinject, BindingEngine, Disposable, observable, transient } from "aurelia-framework";
import { MeasurementSetupWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/MeasurementSetupWorkflowModuleCatalog";
import { IFilter } from "filters/IFilter";
import { MeasurementDeviceSetupFilterFactory } from "filters/MeasurementDeviceSetupFilterFactory";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { MeasurementSetupRepository } from "repositories/MeasurementSetupRepository";
import { MeasurementDeviceSetup, MeasurementDeviceSetupStub } from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { NotificationUrgency } from "services/Notification/INotificationService";

@transient()
@autoinject
export class MeasurementSetupsModule implements IWorkflowModuleLifetime {
    //#region Workflow Properties
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedMeasurementSetupId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private selectedMeasurementDeviceSetupInfoId: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    private measurementDeviceSetupNameFilterString: string;
    //#endregion

    //#region Private Properties
    @observable
    private measurementDeviceSetupNameFilter: IFilter<MeasurementDeviceSetupStub | MeasurementDeviceSetup>;
    private subscriptions: Disposable[];
    private moduleInstance: IWorkflowModuleInstance;
    //#endregion

    constructor(
        private readonly measurementSetupRepository: MeasurementSetupRepository,
        private readonly filterFactory: MeasurementDeviceSetupFilterFactory,
        private readonly bindingEngine: BindingEngine
    ) {}

    //#region Aurelia Callbacks
    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        this.subscriptions = [
            this.moduleInstance.getWorkflow().subscribeModulesChanged(() => {
                this.bindingEngine
                    .propertyObserver(this.moduleInstance.getWorkflow(), "selectedMeasurementSetupId")
                    .subscribe((x) => this.selectedMeasurementSetupIdChanged(x));
                this.bindingEngine
                    .propertyObserver(this.moduleInstance.getWorkflow(), "selectedMeasurementDeviceSetupInfoId")
                    .subscribe((x) => this.selectedMeasurementDeviceSetupInfoIdChanged(x));
                this.bindingEngine
                    .propertyObserver(this.moduleInstance.getWorkflow(), "measurementDeviceSetupNameFilterString")
                    .subscribe((x) => this.measurementDeviceSetupNameFilterStringChanged());
            }),
        ];
    }

    public deactivateModule(): void {
        while (this.subscriptions.length !== 0) {
            this.subscriptions.pop().dispose();
        }
    }

    //#endregion

    //#region User Input Methods

    private selectedMeasurementSetupIdChanged(newValue: string): void {
        if (!this.moduleInstance) return;

        if (newValue !== undefined && newValue !== null) {
            this.moduleInstance
                .getWorkflow()
                .getModules(MeasurementSetupWorkflowModuleCatalog.MeasurementDeviceModule)
                .forEach((x) => x.close());
        }
    }

    private selectedMeasurementDeviceSetupInfoIdChanged(newValue: string): void {
        if (!this.moduleInstance) return;

        if (newValue !== undefined && newValue !== null) {
            this.moduleInstance
                .getWorkflow()
                .getModules(MeasurementSetupWorkflowModuleCatalog.MeasurementDeviceModule)
                .forEach((x) => x.close());
            this.moduleInstance.getWorkflow().addModule(MeasurementSetupWorkflowModuleCatalog.MeasurementDeviceModule);
        }
    }

    private measurementDeviceSetupNameFilterStringChanged(): void {
        if (!this.measurementDeviceSetupNameFilterString) {
            this.measurementDeviceSetupNameFilter = undefined;
        } else {
            this.measurementDeviceSetupNameFilter = this.filterFactory.create((setup) => {
                if (!setup.name && !this.measurementDeviceSetupNameFilterString) return true;

                if (setup.name && setup.name.toLowerCase().indexOf(this.measurementDeviceSetupNameFilterString) !== -1)
                    return true;

                return false;
            });
        }
    }

    private measurementDeviceSetupNameFilterChanged(): void {
        if (!this.measurementDeviceSetupNameFilter) this.measurementDeviceSetupNameFilterString = "";
    }

    public newMeasurementSetup(): void {
        const setup = new MeasurementDeviceSetupStub("New Measurement Setup");

        this.measurementSetupRepository
            .post(setup)
            .then((x) => {
                // Close Measurement Device module if it is open
                this.moduleInstance
                    .getWorkflow()
                    .getModules(MeasurementSetupWorkflowModuleCatalog.MeasurementDeviceModule)
                    .forEach((x) => x.close());

                this.selectedMeasurementSetupId = x.id;
            })
            .catch((e: Error) => {
                this.moduleInstance.createNotification().withUrgency(NotificationUrgency.Error).withMessage(e.message);
            });
    }
    //#endregion
}
